import { Box, Stack, styled, Typography } from '@mui/material';
import kebabCase from 'lodash/kebabCase';

import { HeatStrip } from './HeatStrip';
import { HeatStripMultiProps, HeatStripMultiRow } from './types';
import { mergeSx } from '../../../../utils';
import { NoData } from '../../../placeholders/NoData';

const DEFAULT_ROW_HEIGHT = 96;
const DEFAULT_ROW_GAP = 4;
const DEFAULT_LABEL_WIDTH = 200;
const SHARED_AXIS_HEIGHT = 28;

const RowGrid = styled(Box)({
  display: 'grid',
  alignItems: 'center',
});

type HeatStripMultiRowProps = {
  row: HeatStripMultiRow;
  index: number;
  isLast: boolean;
  xAxisPlacement: HeatStripMultiProps['xAxisPlacement'];
  labelWidth: number;
  rowHeight: number;
  rowGap: number;
  domain: HeatStripMultiProps['domain'];
  binCount?: HeatStripMultiProps['binCount'];
  cellGap?: HeatStripMultiProps['cellGap'];
  cellRadius?: HeatStripMultiProps['cellRadius'];
  cellHeight?: HeatStripMultiProps['cellHeight'];
  colorRamp?: HeatStripMultiProps['colorRamp'];
  overThresholdColorRamp?: HeatStripMultiProps['overThresholdColorRamp'];
  emptyColor?: HeatStripMultiProps['emptyColor'];
  threshold?: HeatStripMultiProps['threshold'];
  thresholdBand?: HeatStripMultiProps['thresholdBand'];
  unit?: HeatStripMultiProps['unit'];
  ticks?: HeatStripMultiProps['ticks'];
  formatTick?: HeatStripMultiProps['formatTick'];
  isLoading?: boolean;
  countLabel?: HeatStripMultiProps['countLabel'];
  testId: string;
};

const HeatStripMultiRowComponent = ({
  row,
  index,
  isLast,
  xAxisPlacement,
  labelWidth,
  rowHeight,
  rowGap,
  domain,
  binCount,
  cellGap,
  cellRadius,
  cellHeight,
  colorRamp,
  overThresholdColorRamp,
  emptyColor,
  threshold,
  thresholdBand,
  unit,
  ticks,
  formatTick,
  isLoading,
  countLabel,
  testId,
}: HeatStripMultiRowProps) => {
  const rowId = kebabCase(String(row.id));
  const showRowAxis =
    xAxisPlacement === 'per-row' || (xAxisPlacement === 'shared' && isLast);

  return (
    <RowGrid
      key={row.id}
      sx={{
        gridTemplateColumns: `${labelWidth}px 1fr`,
        columnGap: 12,
        rowGap: 0,
        mt: index === 0 ? 0 : rowGap,
      }}
      data-testid={`${testId}-row-${rowId}`}
    >
      <Box sx={{ alignSelf: 'center' }}>
        {typeof row.label === 'string' ? (
          <Typography variant="P12R" color="grey.700" component="span">
            {row.label}
          </Typography>
        ) : (
          row.label
        )}
      </Box>
      <Box>
        <HeatStrip
          data={row.data}
          bins={row.bins}
          domain={row.domain ?? domain}
          binCount={row.binCount ?? binCount}
          cellGap={row.cellGap ?? cellGap}
          cellRadius={row.cellRadius ?? cellRadius}
          cellHeight={row.cellHeight ?? cellHeight}
          colorRamp={row.colorRamp ?? colorRamp}
          overThresholdColorRamp={
            row.overThresholdColorRamp ?? overThresholdColorRamp
          }
          emptyColor={row.emptyColor ?? emptyColor}
          getCellColor={row.getCellColor}
          threshold={row.threshold ?? threshold}
          thresholdBand={row.thresholdBand ?? thresholdBand}
          metricMarkers={row.metricMarkers}
          showXAxis={showRowAxis}
          unit={row.unit ?? unit}
          ticks={row.ticks ?? ticks}
          formatTick={row.formatTick ?? formatTick}
          tooltipConfig={row.tooltipConfig}
          showTooltip={row.showTooltip}
          tooltipProps={row.tooltipProps}
          formatTooltipBin={row.formatTooltipBin}
          countLabel={row.countLabel ?? countLabel}
          isLoading={isLoading}
          testId={`${testId}-${rowId}`}
          height={
            showRowAxis
              ? Math.max(80, rowHeight)
              : Math.max(60, rowHeight - SHARED_AXIS_HEIGHT)
          }
        />
      </Box>
    </RowGrid>
  );
};

export const HeatStripMulti = ({
  rows,
  domain,
  binCount,
  cellGap,
  cellRadius,
  cellHeight,
  colorRamp,
  overThresholdColorRamp,
  emptyColor,
  threshold,
  thresholdBand,
  unit,
  ticks,
  formatTick,
  xAxisPlacement = 'per-row',
  xAxisLabel,
  rowHeight = DEFAULT_ROW_HEIGHT,
  rowGap = DEFAULT_ROW_GAP,
  labelWidth = DEFAULT_LABEL_WIDTH,
  isLoading,
  hasNoData,
  noDataOverlayProps,
  title,
  countLabel,
  testId = 'heat-strip-multi',
  sx,
}: HeatStripMultiProps) => {
  const hasNoDataResolved = hasNoData ?? rows.length === 0;
  const shouldRenderRows = rows.length > 0 && (isLoading || !hasNoDataResolved);

  const titleContent = title ? (
    <Box sx={{ mb: 8 }}>
      {typeof title === 'string' ? (
        <Typography variant="P14B">{title}</Typography>
      ) : (
        title
      )}
    </Box>
  ) : null;

  return (
    <Stack data-testid={testId} sx={sx}>
      {titleContent}

      {shouldRenderRows ? (
        rows.map((row, idx) => (
          <HeatStripMultiRowComponent
            key={row.id}
            row={row}
            index={idx}
            isLast={idx === rows.length - 1}
            xAxisPlacement={xAxisPlacement}
            labelWidth={labelWidth}
            rowHeight={rowHeight}
            rowGap={rowGap}
            domain={domain}
            binCount={binCount}
            cellGap={cellGap}
            cellRadius={cellRadius}
            cellHeight={cellHeight}
            colorRamp={colorRamp}
            overThresholdColorRamp={overThresholdColorRamp}
            emptyColor={emptyColor}
            threshold={threshold}
            thresholdBand={thresholdBand}
            unit={unit}
            ticks={ticks}
            formatTick={formatTick}
            isLoading={isLoading}
            countLabel={countLabel}
            testId={testId}
          />
        ))
      ) : (
        <NoData
          testId={`${testId}-no-data`}
          {...noDataOverlayProps}
          sx={mergeSx({ py: 8 }, noDataOverlayProps?.sx)}
        />
      )}

      {xAxisPlacement !== 'none' && xAxisLabel && (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography
            variant="L10M"
            color="textSecondary"
            sx={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}
          >
            {xAxisLabel}
          </Typography>
        </Box>
      )}
    </Stack>
  );
};
